import React, { useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export const money = (n) => (Number(n) || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");
export const inputCls = "bg-slate-950 border-slate-700";

const PILL = {
  Pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Rejected: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  Paid: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Partial: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Outstanding: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

export const StatusPill = ({ status, testid }) => (
  <span data-testid={testid} className={`whitespace-nowrap text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${PILL[status] || PILL.Pending}`}>
    {status}
  </span>
);

export const Panel = ({ title, subtitle, right, children }) => (
  <div className="glass-card rounded-lg p-5 sm:p-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
      <div>
        <div className="font-display text-xl font-bold">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

export const Fld = ({ label, className = "", children }) => (
  <div className={className}>
    <Label className="text-xs text-slate-400">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export function DataTable({ columns, rows, testid, empty = "No records yet." }) {
  return (
    <div data-testid={testid} className="overflow-x-auto rounded-md border border-slate-800">
      <table className="w-full text-sm min-w-[760px]">
        <thead className="bg-slate-900/80 text-slate-400 uppercase text-[10px] tracking-widest">
          <tr>{columns.map((c) => <th key={c.key} className={`px-4 py-2.5 ${c.right ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-slate-500">{empty}</td></tr>}
          {rows.map((r, i) => (
            <tr key={r.id || r.invoice_number || r.client_name || i} data-testid={`${testid}-row-${i}`} className="border-t border-slate-800 hover:bg-slate-900/40">
              {columns.map((c) => (
                <td key={c.key} className={`px-4 py-2.5 ${c.right ? "text-right font-mono-ops" : ""} ${c.mono ? "font-mono-ops text-xs" : ""}`}>
                  {c.render ? c.render(r) : (r[c.key] || "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DecisionDialog({ kind, record, onDone, children }) {
  const [open, setOpen] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/finance/${kind}/${record.id}/decision`, { decision, remarks });
      toast.success(`${record.number} ${decision === "approve" ? "approved" : "rejected"}`);
      setOpen(false); setRemarks(""); onDone();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`button-review-${record.number}`} size="sm" className="h-7 bg-blue-600 hover:bg-blue-500">Review</Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Review {record.number}</DialogTitle></DialogHeader>
        <div className="text-sm space-y-3">{children}</div>
        <Fld label="Remarks (required when rejecting)">
          <Textarea data-testid="input-decision-remarks" rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} className={inputCls} />
        </Fld>
        <DialogFooter className="gap-2">
          <Button data-testid="button-reject" variant="outline" disabled={busy} onClick={() => decide("reject")}
                  className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10">Reject</Button>
          <Button data-testid="button-approve" disabled={busy} onClick={() => decide("approve")}
                  className="bg-emerald-600 hover:bg-emerald-500">Approve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const DecisionCell = ({ r }) => (
  r.decision ? (
    <div className="text-xs">
      <div className="text-slate-300">{r.decision.by_name}</div>
      {r.decision.remarks && <div className="text-slate-500 max-w-[220px] truncate" title={r.decision.remarks}>{r.decision.remarks}</div>}
    </div>
  ) : <span className="text-slate-600 text-xs">—</span>
);
